import styled, { keyframes } from "styled-components";

const fadeSlideIn = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const rowExpand = keyframes`
  from { opacity: 0; transform: translateY(-3px); }
  to   { opacity: 1; transform: translateY(0); }
`;

/* ═══════════════════════════════════════════════════════════════════════
   UNCHANGED — page shell, summary cards.
═══════════════════════════════════════════════════════════════════════ */

export const MainDiv = styled.div`
  display: flex;
  flex-direction: row;
  flex-grow: 1;
  background: #F0F2FA;
  overflow: hidden;
`;

export const ContentWrapDiv = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
  padding: 28px 32px 24px 32px;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  font-family: 'DM Sans', 'Segoe UI', sans-serif;
  box-sizing: border-box;
  border-top: 5px solid #6366F1;
`;

export const TopContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 20px;
  flex-shrink: 0;
  gap: 12px;
  flex-wrap: wrap;

  .left { display: flex; flex-direction: column; gap: 3px; max-width: 640px; }

  .page-title {
    font-weight: 900;
    font-size: 30px;
    line-height: 1.1;
    color: #0F172A;
    letter-spacing: -0.6px;
  }

  .page-subtitle {
    font-size: 13px;
    color: #64748B;
    font-weight: 600;
    line-height: 1.5;
  }
`;

export const FilterRow = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: nowrap;
  align-items: center;
  margin-bottom: 16px;
  flex-shrink: 0;
  white-space: nowrap;

  overflow-x: auto;
  overflow-y: hidden;

  scrollbar-width: thin;
  scrollbar-color: #d1d5db transparent;

  &::-webkit-scrollbar { height: 4px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb { background: #d1d5db; border-radius: 10px; }
  &::-webkit-scrollbar-thumb:hover { background: #bfc5cd; }
`;

export const StyledSelectWrap = styled.div`
  .ant-select-selector {
    height: 42px !important;
    border-radius: 12px !important;
    border: 1px solid #D7DDED !important;
    display: flex; align-items: center;
    padding: 0 12px !important;
    box-shadow: none !important;
  }
  .ant-select-selection-item,
  .ant-select-selection-placeholder,
  .ant-select-selection-search-input {
    height: 40px !important; line-height: 40px !important;
    display: flex; align-items: center;
    font-size: 14px; font-weight: 500;
  }
  .ant-select-arrow { color: #94A3B8; }
  .ant-select-focused .ant-select-selector {
    border-color: #818CF8 !important;
    box-shadow: 0 0 0 3px rgba(99,102,241,0.08) !important;
  }
  .ant-select-selector:hover { border-color: #A5B4FC !important; }
`;

export const StyledSearchWrap = styled.div`
  .ant-input-affix-wrapper {
    height: 42px;
    border-radius: 12px;
    border: 1px solid #D7DDED;
    padding: 0 12px;
    box-shadow: none;
  }
  .ant-input-affix-wrapper:hover { border-color: #A5B4FC; }
  .ant-input-affix-wrapper-focused {
    border-color: #818CF8 !important;
    box-shadow: 0 0 0 3px rgba(99,102,241,0.08) !important;
  }
  .ant-input {
    font-size: 14px;
    font-weight: 500;
    color: #334155;
  }
  .anticon { color: #94A3B8; }
`;

export const ClearFiltersButton = styled.button`
  height: 42px;
  padding: 0 16px;
  border-radius: 12px;
  border: 1.5px solid #E2E5F5;
  background: #fff;
  color: #64748B;
  font-weight: 700;
  font-size: 13px;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.18s ease;

  &:hover {
    border-color: #A5B4FC;
    color: #4F46E5;
    background: #EEF2FF;
  }
`;

export const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
  margin-bottom: 16px;
  flex-shrink: 0;

  @media (max-width: 640px) { grid-template-columns: 1fr; }
`;

export const SummaryCard = styled.div`
  background: #fff;
  border-radius: 14px;
  padding: 16px 18px 14px;
  position: relative;
  overflow: hidden;
  box-shadow: 0 1px 2px rgba(0,0,0,0.04), 0 4px 16px rgba(0,0,0,0.04);
  border: 1px solid ${({ color }) => color}22;
  animation: ${fadeSlideIn} 0.3s ease both;
  animation-delay: ${({ idx }) => (idx || 0) * 0.06}s;
  transition: transform 0.18s ease, box-shadow 0.18s ease;

  &::before {
    content: "";
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 3px;
    background: ${({ color }) => color};
    border-radius: 14px 14px 0 0;
  }

  &::after {
    content: "";
    position: absolute;
    bottom: -12px; right: -12px;
    width: 72px; height: 72px;
    border-radius: 50%;
    background: ${({ color }) => color}10;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 24px ${({ color }) => color}1F;
  }
`;

export const SummaryValue = styled.div`
  font-size: 32px;
  font-weight: 800;
  color: ${({ color }) => color};
  line-height: 1;
  letter-spacing: -1.2px;
  margin-bottom: 6px;
  font-variant-numeric: tabular-nums;
  position: relative;
  z-index: 1;
`;

export const SummaryLabel = styled.div`
  font-size: 9.5px;
  color: #94A3B8;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
  position: relative;
  z-index: 1;
`;

export const ModalMessage = styled.p`
  font-size: 14px;
  color: #4b5563;
  margin: 0 0 8px 0;
  line-height: 1.6;
`;

export const ModalRecruiterName = styled.p`
  font-size: 14px;
  font-weight: 700;
  color: #0F172A;
  margin: 0;
`;

export const RecruiterMainDiv = styled.div`
  display: flex;
  flex-direction: row;
  flex-grow: 1;
  background: #F0F2FA;
  overflow: hidden;
`;

export const RecruiterContentWrapDiv = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
  padding: 28px 32px 24px 32px;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  font-family: 'DM Sans', 'Segoe UI', sans-serif;
  box-sizing: border-box;
  border-top: 5px solid #6366F1;
`;

export const RecruiterTopContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 20px;
  flex-shrink: 0;
  gap: 12px;
  flex-wrap: wrap;

  .left { display: flex; flex-direction: column; gap: 3px; max-width: 640px; }

  .page-title {
    font-weight: 900;
    font-size: 30px;
    line-height: 1.1;
    color: #0F172A;
    letter-spacing: -0.6px;
  }

  .page-subtitle {
    font-size: 13px;
    color: #64748B;
    font-weight: 600;
    line-height: 1.5;
  }
`;

/* ═══════════════════════════════════════════════════════════════════════
   Table (unchanged from the redesign) + NEW: status toggle-buttons,
   table header row with Add User button, form styling for the Add User
   modal.
═══════════════════════════════════════════════════════════════════════ */

export const TableScrollArea = styled.div`
  border: 1px solid #EEF0F6;
  border-radius: 16px;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.03), 0 8px 24px rgba(15, 23, 42, 0.05);
  background: #fff;
  overflow-y: auto;
  overflow-x: auto;
  flex: 1;
  min-height: 0;

  &::-webkit-scrollbar { width: 6px; height: 6px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb { background: #DCE0F5; border-radius: 10px; }
  &::-webkit-scrollbar-thumb:hover { background: #C7CEF0; }
`;

// export const RecruiterMainTable = styled.table`
//   width: 100%;
//   min-width: 760px;
//   border-collapse: separate;
//   border-spacing: 0;
//   font-size: 13.5px;
//   table-layout: fixed;

//   thead {
//     position: sticky;
//     top: 0;
//     z-index: 10;
//   }

//   th, td {
//     padding: 0 20px;
//     vertical-align: middle;
//   }

//   tbody tr:last-child td { border-bottom: 0 !important; }
// `;

export const RecruiterMainTable = styled.table`
  width: 100%;
  min-width: 760px;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 13.5px;
  table-layout: fixed;

  thead {
    position: sticky;
    top: 0;
    z-index: 10;
  }

  th, td {
    padding: 0 20px;
    text-align: center;      /* ← add this line */
    vertical-align: middle;
  }

  tbody tr:last-child td { border-bottom: 0 !important; }
`;

export const THMeta = styled.th`
  background: linear-gradient(180deg, #FAFBFF 0%, #F5F6FC 100%);
  height: 46px;
  font-weight: 700;
  font-size: 10.5px;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: #8B93A7;
  text-align: ${({ left }) => (left ? "left" : "center")};
  white-space: nowrap;
  vertical-align: middle;
  border-bottom: 1px solid #ECEEF6;

  &:first-child { border-top-left-radius: 16px; }
  &:last-child { border-top-right-radius: 16px; }
`;

export const DataRow = styled.tr`
  transition: background-color 0.15s ease;

  td {
    background: #fff;
    height: 60px;
    border-bottom: 1px solid #F2F3F9;
    color: #334155;
    transition: background-color 0.15s ease;
  }

  &:hover td {
    background: #FAFBFF;
  }

  animation: ${rowExpand} 0.18s ease both;

  &[data-inactive="true"] td {
    background: #FCFCFE;
  }
  &[data-inactive="true"]:hover td {
    background: #F6F7FB;
  }
`;

export const RecruiterCell = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;

  .name {
    font-weight: 700;
    font-size: 13.5px;
    color: #0F172A;
    letter-spacing: -0.1px;
  }

  .email {
    font-size: 11.5px;
    color: #94A3B8;
    font-weight: 500;
  }
`;

export const RecruiterAvatar = styled.div`
  width: 34px;
  height: 34px;
  border-radius: 10px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12.5px;
  font-weight: 800;
  color: #ffffff;
  letter-spacing: 0.2px;
  background: ${({ bg }) => bg || "linear-gradient(135deg, #818CF8, #6366F1)"};
  box-shadow: 0 2px 6px rgba(99, 102, 241, 0.25);
`;

export const EmailText = styled.span`
  font-size: 12.5px;
  color: #64748B;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: block;
  max-width: 100%;
`;

export const DateText = styled.span`
  font-size: 12px;
  color: #94A3B8;
  font-weight: 600;
  white-space: nowrap;
`;

export const AccessPillWrap = styled.div`
  display: inline-flex;

  .ant-select {
    width: auto;
    min-width: 148px;
  }

  .ant-select-selector {
    height: 30px !important;
    border-radius: 999px !important;
    border: 1px solid ${({ $border }) => $border || "#E2E5F5"} !important;
    background: ${({ $bg }) => $bg || "#F5F6FC"} !important;
    display: flex !important;
    align-items: center !important;
    padding: 0 12px !important;
    box-shadow: none !important;
    transition: all 0.15s ease;
  }

  .ant-select-selection-item {
    height: 28px !important;
    line-height: 28px !important;
    font-size: 12.5px !important;
    font-weight: 700 !important;
    color: ${({ $color }) => $color || "#334155"} !important;
    padding-right: 14px !important;
  }

  .ant-select-arrow {
    color: ${({ $color }) => $color || "#334155"} !important;
    opacity: 0.7;
    font-size: 10px;
  }

  .ant-select:hover .ant-select-selector {
    filter: brightness(0.97);
  }

  .ant-select-focused .ant-select-selector {
    box-shadow: 0 0 0 3px ${({ $border }) => $border || "#E2E5F5"}80 !important;
  }

  .ant-select-disabled .ant-select-selector {
    opacity: 0.6;
  }
`;

export const StatusControl = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 10px;
`;

export const StatusPill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px 4px 8px;
  border-radius: 999px;
  font-size: 11.5px;
  font-weight: 700;
  white-space: nowrap;
  background: ${({ active }) => (active ? "#ECFDF5" : "#F8FAFC")};
  color: ${({ active }) => (active ? "#047857" : "#94A3B8")};
  border: 1px solid ${({ active }) => (active ? "#A7F3D0" : "#E2E8F0")};

  .dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: ${({ active }) => (active ? "#10B981" : "#94A3B8")};
    box-shadow: ${({ active }) => (active ? "0 0 0 3px rgba(16,185,129,0.15)" : "none")};
  }
`;

export const StatusToggleTrack = styled.div`
  position: relative;
  width: 34px;
  height: 20px;
  border-radius: 999px;
  flex-shrink: 0;
  cursor: ${({ $loading }) => ($loading ? "not-allowed" : "pointer")};
  background: ${({ active, $loading }) =>
    $loading ? "#D9DCE8" : active ? "linear-gradient(135deg, #34D399, #10B981)" : "#E2E5F0"};
  transition: background 0.2s ease;
  box-shadow: ${({ active, $loading }) =>
    active && !$loading ? "0 0 0 3px rgba(16,185,129,0.12)" : "none"};
`;

export const StatusToggleThumb = styled.div`
  position: absolute;
  top: 2px;
  left: ${({ active }) => (active ? "16px" : "2px")};
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #fff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.25);
  transition: left 0.2s cubic-bezier(.4,0,.2,1);
`;

export const SpinWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 240px;
`;

export const EmptyWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 0;
  color: #94A3B8;
  font-size: 14px;
  gap: 12px;

  .icon { font-size: 40px; }
`;

export const PaginationRow = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 16px 4px 4px;
  margin-top: 4px;
  border-top: 1px solid #F1F2F6;
  flex-shrink: 0;
`;

/* ─── NEW: status toggle buttons (replaces the All/Active/Inactive select) ─ */
export const ToggleGroup = styled.div`
  display: inline-flex;
  background: #F0F2F8;
  border-radius: 12px;
  padding: 3px;
  gap: 2px;
  flex-shrink: 0;
`;

export const ToggleButton = styled.button`
  height: 36px;
  padding: 0 16px;
  border-radius: 9px;
  border: none;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: all 0.15s ease;

  background: ${({ active, $variant }) =>
    active ? ($variant === "inactive" ? "#fff" : "#fff") : "transparent"};
  color: ${({ active, $variant }) =>
    active
      ? $variant === "inactive"
        ? "#64748B"
        : "#047857"
      : "#94A3B8"};
  box-shadow: ${({ active }) => (active ? "0 1px 3px rgba(15,23,42,0.10)" : "none")};

  .dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: ${({ $variant }) => ($variant === "inactive" ? "#94A3B8" : "#10B981")};
    opacity: ${({ active }) => (active ? 1 : 0.5)};
  }

  &:hover {
    color: ${({ $variant }) => ($variant === "inactive" ? "#475569" : "#059669")};
  }
`;

/* ─── NEW: table header row (Add User button sits top-right of the table) ─ */
export const TableHeaderRow = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-bottom: 12px;
  flex-shrink: 0;
`;

export const AddUserButton = styled.button`
  height: 42px;
  padding: 0 18px;
  border-radius: 12px;
  border: none;
  background: linear-gradient(135deg, #6366F1, #4F46E5);
  color: #fff;
  font-weight: 700;
  font-size: 13.5px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  box-shadow: 0 4px 14px rgba(79, 70, 229, 0.28);
  transition: all 0.18s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 6px 18px rgba(79, 70, 229, 0.35);
  }

  &:active {
    transform: translateY(0);
  }
`;

/* ─── NEW: form field styling for the Add User modal ─────────────────────── */
export const StyledFormWrap = styled.div`
  .ant-form-item-label > label {
    font-size: 12.5px;
    font-weight: 700;
    color: #475569;
  }

  .ant-input,
  .ant-input-affix-wrapper,
  .ant-select-selector {
    border-radius: 10px !important;
    // border: 1px solid #E2E5F5 !important;
    box-shadow: none !important;
  }

  .ant-input:hover,
  .ant-input-affix-wrapper:hover,
  .ant-select-selector:hover {
    border-color: #A5B4FC !important;
  }

  .ant-input:focus,
  .ant-input-affix-wrapper-focused,
  .ant-select-focused .ant-select-selector {
    border-color: #818CF8 !important;
    box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.08) !important;
  }
`;